/**
 * Ingredients explorer.
 *
 * Deliberately dependency-free (no @theme/component import) so the section can
 * carry its own <script> tag and nothing has to be registered in theme.liquid
 * or the import map.
 *
 * Desktop: ingredient names are buttons that swap the panel over the flat lay.
 * Mobile: the same ingredients render as a scroll-snapped deck, and the only
 * work here is keeping the progress bar and the card caption heights honest.
 */

/* Matches the 767.98px breakpoint the section's CSS uses to swap the chip list
   for the mobile deck. Above it the chips exist; below they are display:none. */
const DESKTOP_QUERY = '(min-width: 768px)';
const DEMO_START_DELAY = 3000;
const DEMO_STEP_DELAY = 5000;

class IngredientsExplorer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialised === 'true') return;
    this.dataset.initialised = 'true';

    this.data = this.#readData();
    this.chips = [...this.querySelectorAll('.plant-based-ingredients__chip')];
    this.detail = this.#ref('detail');
    this.detailImage = this.#ref('detailImage');
    this.detailName = this.#ref('detailName');
    this.detailBenefit = this.#ref('detailBenefit');
    this.caption = this.#ref('caption');
    this.live = this.#ref('live');
    this.track = this.#ref('track');
    this.progress = this.#ref('progress');
    this.deck = this.querySelector('.plant-based-ingredients__deck');

    this.#bindChips();
    this.#bindDismiss();

    if (this.track && this.progress) {
      this.track.addEventListener('scroll', this.#updateProgress, { passive: true });
      this.#updateProgress();
    }

    window.addEventListener('resize', this.#onResize);

    // The deck sits well below the fold, so its cards are usually still
    // unsized when this runs. Watch the track and measure once it has a real
    // width — width only, since setting the caption height changes the height
    // and would otherwise feed back into another measurement.
    if (this.track && 'ResizeObserver' in window) {
      this.trackObserver = new ResizeObserver(() => {
        const width = Math.round(this.track.getBoundingClientRect().width);
        if (width === this.lastTrackWidth) return;
        this.lastTrackWidth = width;
        this.#remeasure();
      });
      this.trackObserver.observe(this.track);
    }

    this.#setUpDemo();

    this.#remeasure();
    // Web fonts change the wrap points, so re-measure once they land.
    if (document.fonts?.ready) document.fonts.ready.then(() => this.#remeasure());

    // Preload the cut-outs so the first pick doesn't show an empty frame.
    for (const item of Object.values(this.data)) {
      if (item.image) new Image().src = item.image;
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    this.trackObserver?.disconnect();
    clearTimeout(this.resizeTimer);
    this.#stopDemo();
  }

  #ref(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  #readData() {
    const node = this.#ref('data');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  #bindChips() {
    this.chips.forEach((chip, index) => {
      chip.addEventListener('click', () => {
        // A real pick ends the demo for good - it must never fight the user.
        this.#stopDemo();
        if (chip.getAttribute('aria-pressed') === 'true') {
          this.#clear();
          return;
        }
        this.#select(chip.dataset.ingredient);
      });

      chip.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        const next = this.chips[(index + step + this.chips.length) % this.chips.length];
        next?.focus();
      });
    });
  }

  #bindDismiss() {
    this.#ref('close')?.addEventListener('click', () => {
      this.#stopDemo();
      this.#clear();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.hasAttribute('data-active')) return;
      this.#stopDemo();
      this.#clear();
    });
  }

  /**
   * `announce` is false for demo steps: the live region exists to report a
   * choice the user made, and firing it every five seconds unprompted would
   * make the section unusable with a screen reader.
   */
  #select(id, { announce = true } = {}) {
    const item = this.data[id];
    if (!item) return;

    this.chips.forEach((chip) => {
      chip.setAttribute('aria-pressed', String(chip.dataset.ingredient === id));
    });

    const apply = () => {
      if (item.image) {
        this.detailImage.src = item.image;
        this.detailImage.alt = item.name;
      }
      this.detailName.textContent = item.name;
      this.detailBenefit.textContent = item.benefit;
      this.detail.classList.remove('is-swapping');
    };

    // Fade the outgoing cut-out only when one is already showing.
    if (this.hasAttribute('data-active')) {
      this.detail.classList.add('is-swapping');
      setTimeout(apply, 160);
    } else {
      apply();
    }

    this.setAttribute('data-active', id);
    if (announce && this.live) this.live.textContent = `${item.name}: ${item.benefit}`;
  }

  #clear() {
    this.chips.forEach((chip) => chip.setAttribute('aria-pressed', 'false'));
    this.removeAttribute('data-active');
    if (this.live) this.live.textContent = '';
  }

  /**
   * Thumb width is the visible fraction of the track; it travels the remaining
   * (1 - scale) of the bar. With transform-origin:left the composite maps the
   * right edge to (scale + translate) * width, so translate maxes at 1 - scale.
   */
  #updateProgress = () => {
    const { scrollWidth, clientWidth, scrollLeft } = this.track;
    const max = scrollWidth - clientWidth;
    const ratio = max > 0 ? Math.min(1, Math.max(0, scrollLeft / max)) : 0;
    const scale = clientWidth / scrollWidth;
    this.progress.style.transform = `translateX(${ratio * (1 - scale) * 100}%) scaleX(${scale})`;
  };

  /**
   * Give every card's teal band the depth of the deepest one, so the bands line
   * up across cards however long each benefit runs. Measured rather than
   * hard-coded: the copy is editable per product, so a fixed height would drift.
   */
  #equalizeCards() {
    if (!this.deck || this.deck.offsetParent === null) return; // desktop: deck is display:none
    const cards = [...this.querySelectorAll('.plant-based-ingredients__card')];
    if (!cards.length) return;

    // Bail until the cards have a real size. The images are lazy, so this can
    // run while the deck is still laid out at the desktop width, and measuring
    // there produces a caption far taller than the card it has to fit in.
    const cardHeight = cards[0].getBoundingClientRect().height;
    if (cardHeight < 1) return;

    // Collapse first: the height is applied through the variable, so measuring
    // without clearing it would only ever ratchet upwards.
    this.deck.style.setProperty('--pbi-caption-height', '0px');
    let tallest = 0;
    for (const card of cards) {
      const caption = card.querySelector('.plant-based-ingredients__card-caption');
      if (caption) tallest = Math.max(tallest, caption.offsetHeight);
    }

    // A band can never be allowed to swallow its own card, whatever a
    // mid-layout measurement claims.
    tallest = Math.min(tallest, Math.round(cardHeight * 0.6));
    this.deck.style.setProperty('--pbi-caption-height', `${tallest}px`);
  }

  /**
   * Same idea on desktop, where one panel swaps between ingredients: reserve the
   * deepest benefit's height so the image above it doesn't resize on every pick.
   * The panel is visibility:hidden but still laid out, so this measures without
   * anything flashing on screen.
   */
  #equalizeDetail() {
    if (!this.caption) return;
    if (this.deck && this.deck.offsetParent !== null) return; // mobile: deck is what's showing

    const name = this.detailName.textContent;
    const benefit = this.detailBenefit.textContent;
    this.caption.style.minHeight = '';

    let tallest = 0;
    for (const item of Object.values(this.data)) {
      this.detailName.textContent = item.name;
      this.detailBenefit.textContent = item.benefit;
      tallest = Math.max(tallest, this.caption.offsetHeight);
    }

    this.detailName.textContent = name;
    this.detailBenefit.textContent = benefit;
    this.caption.style.minHeight = `${tallest}px`;
  }

  #remeasure() {
    this.#equalizeCards();
    this.#equalizeDetail();
  }

  #onResize = () => {
    if (this.track && this.progress) this.#updateProgress();
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.#remeasure(), 120);
  };

  /**
   * Autoplay demo.
   *
   * "Click one to see what it does" is easy to miss, so once the section
   * scrolls into view the panel steps through the oils on its own. It runs a
   * single pass and settles back to the default state - the point is to show
   * the chips are live, which lands in the first couple of swaps; looping past
   * that is just movement in the corner of the page.
   *
   * Desktop only, and only for the oils: the actives group is two chips
   * sitting directly under a list that has just demonstrated itself, and
   * including them would stretch the pass past a minute.
   */
  #setUpDemo() {
    this.demoChips = this.chips.filter((chip) => chip.dataset.group !== 'active');
    if (this.demoChips.length < 2 || !('IntersectionObserver' in window)) return;

    this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
    // Nothing here is essential, so anyone who has asked for less motion just
    // gets the static section.
    if (!this.desktopQuery.matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.desktopQuery.addEventListener('change', this.#onDemoBreakpointChange);

    this.demoObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.demoObserver.disconnect();
        this.demoObserver = null;
        this.demoTimer = setTimeout(this.#startDemo, DEMO_START_DELAY);
      },
      { threshold: 0.35 }
    );
    this.demoObserver.observe(this);

    // Hovering or tabbing in means they are reading; hold the current oil
    // until they leave rather than pulling it out from under them.
    this.addEventListener('mouseenter', this.#pauseDemo);
    this.addEventListener('mouseleave', this.#resumeDemo);
    this.addEventListener('focusin', this.#pauseDemo);
    this.addEventListener('focusout', this.#onDemoFocusOut);
  }

  #startDemo = () => {
    this.demoIndex = 0;
    // data-demo keeps the badge up: it steps aside for a real choice, not for
    // the demo, which would otherwise bury it three seconds in on every visit.
    this.setAttribute('data-demo', '');
    this.#demoStep();
  };

  #demoStep = () => {
    const chip = this.demoChips[this.demoIndex];
    if (!chip) {
      this.#stopDemo();
      this.#clear();
      return;
    }
    this.#select(chip.dataset.ingredient, { announce: false });
    this.demoIndex += 1;
    this.demoTimer = setTimeout(this.#demoStep, DEMO_STEP_DELAY);
  };

  #pauseDemo = () => {
    if (!this.hasAttribute('data-demo')) return;
    clearTimeout(this.demoTimer);
    this.demoTimer = null;
  };

  #resumeDemo = () => {
    if (!this.hasAttribute('data-demo') || this.demoTimer) return;
    this.demoTimer = setTimeout(this.#demoStep, DEMO_STEP_DELAY);
  };

  // focusout fires while moving between chips inside the section too, so only
  // resume once focus has actually left.
  #onDemoFocusOut = (event) => {
    if (this.contains(event.relatedTarget)) return;
    this.#resumeDemo();
  };

  // Narrowing to mobile mid-pass would leave an oil selected against a chip
  // list that is now display:none.
  #onDemoBreakpointChange = (event) => {
    if (event.matches) return;
    this.#stopDemo();
    this.#clear();
  };

  #stopDemo = () => {
    clearTimeout(this.demoTimer);
    this.demoTimer = null;
    this.demoObserver?.disconnect();
    this.demoObserver = null;
    this.removeAttribute('data-demo');
    this.desktopQuery?.removeEventListener('change', this.#onDemoBreakpointChange);
    this.removeEventListener('mouseenter', this.#pauseDemo);
    this.removeEventListener('mouseleave', this.#resumeDemo);
    this.removeEventListener('focusin', this.#pauseDemo);
    this.removeEventListener('focusout', this.#onDemoFocusOut);
  };
}

if (!customElements.get('ingredients-explorer')) {
  customElements.define('ingredients-explorer', IngredientsExplorer);
}
